import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import BulletinRichTextEditor from './BulletinRichTextEditor';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../api/client';
import { BOARD_MANUAL_PATH } from '../constants/routes';
import { presignAndUploadBulletinFile, isBulletinUploadConfiguredError } from '../api/bulletinUpload';
import { useAppSnackbar } from '../snackbar/useAppSnackbar';
import type { BulletinAttachmentPayload } from '../constants/bulletin';

const MAX_FILES = 5;

const BulletinNew: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useAppSnackbar();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  /** 無期限がデフォルト */
  const [displayExpiresMode, setDisplayExpiresMode] = useState<'unlimited' | 'one_month'>('unlimited');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const addFiles = (list: FileList | null) => {
    if (!list?.length) return;
    const next = [...files];
    for (let i = 0; i < list.length; i += 1) {
      if (next.length >= MAX_FILES) break;
      next.push(list[i]);
    }
    setFiles(next);
  };

  const submit = async () => {
    const t = title.trim();
    if (!t) {
      setError('タイトルを入力してください');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const attachments: BulletinAttachmentPayload[] = [];
      for (const f of files) {
        try {
          const meta = await presignAndUploadBulletinFile(f);
          attachments.push(meta);
        } catch (e: unknown) {
          if (isBulletinUploadConfiguredError(e)) {
            showSnackbar({
              message: 'ファイル添付は現在利用できません。本文のみ投稿する場合は添付を削除してください。',
              severity: 'error',
            });
            setSaving(false);
            return;
          }
          const msg =
            axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
              ? (e.response.data as { error?: string }).error
              : null;
          showSnackbar({ message: msg || 'ファイルのアップロードに失敗しました', severity: 'error' });
          setSaving(false);
          return;
        }
      }

      await apiClient.post('/api/bulletin/posts', {
        title: t,
        body,
        attachments,
        display_expires_mode: displayExpiresMode,
      });
      showSnackbar({ message: '投稿しました', severity: 'success' });
      navigate(BOARD_MANUAL_PATH);
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
          ? (e.response.data as { error?: string }).error
          : null;
      showSnackbar({ message: msg || '投稿に失敗しました', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 960, width: '100%', mx: 'auto', flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        掲示板に投稿
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <TextField
          label="タイトル"
          fullWidth
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
        />
        <BulletinRichTextEditor
          label="本文"
          value={body}
          onChange={setBody}
          minRows={8}
        />

        <FormControl component="fieldset" sx={{ mb: 2, mt: 1 }}>
          <FormLabel component="legend">表示期限</FormLabel>
          <RadioGroup
            row
            value={displayExpiresMode}
            onChange={(e) =>
              setDisplayExpiresMode(e.target.value === 'one_month' ? 'one_month' : 'unlimited')
            }
            sx={{ flexWrap: 'wrap', gap: { xs: 0, sm: 1 }, columnGap: 2 }}
          >
            <FormControlLabel value="unlimited" control={<Radio />} label="無期限" />
            <FormControlLabel
              value="one_month"
              control={<Radio />}
              label="投稿から1ヶ月で表示終了"
            />
          </RadioGroup>
        </FormControl>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          添付（最大 {MAX_FILES} 件）
        </Typography>
        <Button variant="outlined" component="label" size="small" sx={{ mb: 1 }} disabled={files.length >= MAX_FILES}>
          ファイルを選択
          <input
            type="file"
            hidden
            multiple
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </Button>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {files.map((f, i) => (
            <Chip
              key={`${f.name}-${i}`}
              label={f.name}
              onDelete={() => setFiles(files.filter((_, j) => j !== i))}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={() => void submit()} disabled={saving}>
            投稿する
          </Button>
          <Button variant="text" onClick={() => navigate(BOARD_MANUAL_PATH)} disabled={saving}>
            キャンセル
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default BulletinNew;
